import { useEffect, useState } from "react";
import { Ban, CheckCircle2, Gavel, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { Textarea } from "@/components/ui/Textarea";
import { supabase } from "@/lib/supabase";

type DisputeStatus = "open" | "resolved_a" | "resolved_b" | "void";
type Resolution = "resolved_a" | "resolved_b" | "void";

interface DisputeRow {
  id: string;
  match_id: string | null;
  team_a_id: string | null;
  team_b_id: string | null;
  team_a_claim: string | null;
  team_b_claim: string | null;
  evidence_urls: string[] | null;
  status: DisputeStatus;
  resolution_notes: string | null;
  created_at: string;
}

/**
 * Admin → Disputes. Compete match result disagreements that couldn't
 * be resolved by dual-captain confirmation or peer vote. Admin picks
 * a winner (or voids the result) and leaves a short rationale.
 */
export function AdminDisputes() {
  const [rows, setRows] = useState<DisputeRow[] | null>(null);
  const [status, setStatus] = useState<DisputeStatus | "all">("open");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [resolution, setResolution] = useState<Resolution>("resolved_a");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    let q = supabase
      .from("match_disputes")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (status !== "all") q = q.eq("status", status);
    const { data, error } = await q;
    if (error) {
      toast.error(error.message);
      setRows([]);
      return;
    }
    setRows((data ?? []) as DisputeRow[]);
  }

  useEffect(() => {
    setRows(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function submit() {
    if (!activeId) return;
    setSubmitting(true);
    const { error } = await supabase
      .from("match_disputes")
      .update({
        status: resolution,
        resolution_notes: notes.trim() || null,
        resolved_at: new Date().toISOString(),
      })
      .eq("id", activeId);
    if (error) {
      setSubmitting(false);
      return toast.error(error.message);
    }
    await supabase.rpc("log_audit", {
      action: `match_dispute_${resolution}`,
      entity_type: "match_dispute",
      entity_id: activeId,
      details: { resolution_notes: notes.trim() || null },
    });
    toast.success("Resolved");
    setActiveId(null);
    setNotes("");
    setResolution("resolved_a");
    setSubmitting(false);
    load();
  }

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Disputes
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">Disputes</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Compete match results where dual-captain confirmation and
          peer voting couldn't resolve. Pick a winner or void the
          result with a short rationale.
        </p>
      </header>

      <StatusTabs value={status} onChange={setStatus} />

      {rows === null ? (
        <PageLoader />
      ) : rows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="flex flex-col gap-4">
          {rows.map((r) => (
            <article
              key={r.id}
              className="rounded-2xl border border-border bg-card p-5"
            >
              <header className="mb-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Match {r.match_id?.slice(0, 8) ?? "-"}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Raised {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <StatusPill status={r.status} />
              </header>

              <div className="grid gap-3 sm:grid-cols-2">
                <ClaimCard
                  label="Team A"
                  teamId={r.team_a_id}
                  claim={r.team_a_claim}
                />
                <ClaimCard
                  label="Team B"
                  teamId={r.team_b_id}
                  claim={r.team_b_claim}
                />
              </div>

              {r.evidence_urls && r.evidence_urls.length > 0 && (
                <div className="mt-4">
                  <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Evidence
                  </p>
                  <ul className="flex flex-wrap gap-2">
                    {r.evidence_urls.map((u) => (
                      <li key={u}>
                        <a
                          href={u}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-volt underline underline-offset-2"
                        >
                          {u.slice(0, 40)}…
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {r.status === "open" ? (
                <div className="mt-4 flex justify-end">
                  <Button onClick={() => setActiveId(r.id)}>
                    <Gavel className="h-3 w-3" />
                    Resolve
                  </Button>
                </div>
              ) : (
                r.resolution_notes && (
                  <div className="mt-4 rounded-lg border border-border bg-background p-3">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Resolution notes
                    </p>
                    <p className="text-xs text-muted-foreground">{r.resolution_notes}</p>
                  </div>
                )
              )}
            </article>
          ))}
        </div>
      )}

      {activeId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={(e) => e.target === e.currentTarget && setActiveId(null)}
        >
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Resolve dispute</h2>
              <button
                type="button"
                onClick={() => setActiveId(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mb-4 flex flex-col gap-2">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Outcome
              </label>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    { v: "resolved_a", label: "Team A wins" },
                    { v: "resolved_b", label: "Team B wins" },
                    { v: "void", label: "Void result" },
                  ] as const
                ).map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setResolution(o.v)}
                    className={
                      "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                      (resolution === o.v
                        ? "bg-volt text-primary-foreground"
                        : "bg-secondary text-muted-foreground hover:bg-volt-subtle hover:text-foreground")
                    }
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Resolution notes (visible to both captains)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Short rationale…"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActiveId(null)}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={submitting}>
                {submitting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Gavel className="h-3 w-3" />
                )}
                Submit
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ClaimCard({
  label,
  teamId,
  claim,
}: {
  label: string;
  teamId: string | null;
  claim: string | null;
}) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label} {teamId ? `· ${teamId.slice(0, 8)}…` : ""}
      </p>
      <p className="text-xs">
        {claim ?? <span className="text-muted-foreground">No claim filed.</span>}
      </p>
    </div>
  );
}

function StatusTabs({
  value,
  onChange,
}: {
  value: DisputeStatus | "all";
  onChange: (v: DisputeStatus | "all") => void;
}) {
  const options: Array<{ label: string; value: DisputeStatus | "all" }> = [
    { label: "Open", value: "open" },
    { label: "Resolved", value: "all" },
    { label: "Void", value: "void" },
  ];
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={
            "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
            (value === o.value
              ? "bg-volt text-primary-foreground"
              : "bg-secondary text-muted-foreground hover:bg-volt-subtle hover:text-foreground")
          }
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function StatusPill({ status }: { status: DisputeStatus }) {
  if (status === "open") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-volt-subtle px-2 py-0.5 text-[10px] font-bold text-volt">
        Open
      </span>
    );
  }
  if (status === "void") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
        <Ban className="h-2.5 w-2.5" /> Void
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
      <CheckCircle2 className="h-2.5 w-2.5" />{" "}
      {status === "resolved_a" ? "Team A" : "Team B"}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/40 p-10 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-volt-subtle text-volt">
        <Gavel className="h-5 w-5" />
      </div>
      <p className="text-sm font-semibold">No disputes</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Disputes raised from the app's Compete flow show up here.
      </p>
    </div>
  );
}
